// routes/InboxPane.jsx — 수신함 메일박스 (v5 §12 시안 2b).
//
// 수신함은 **포인터만** 쌓인다. 보낸 사람도 본문 미리보기도 오지 않는다 — 그게 설계다
// (사일런트 푸시). 시안의 그 두 열은 그래서 없다. Task 제목만 task-board 를 한 번 읽어
// 얹는다(N+1 아님 — 배치 하나).
//
//   D-6  raw 사건 코드는 사람 문장 자리에 쓰지 않는다 — 사전에 없는 것은 t-mono 로 드러낸다.
//   D-7  값이 없으면 열을 그리지 않는다. 무동작 버튼을 만들지 않는다.
//   E-4  마지막 빈 페이지를 전체의 빈 상태로 말하지 않는다 — cursor trail 보존.
import { useEffect, useRef, useState } from 'preact/hooks';
import { command, read } from '../api/client.js';
import { WriteBoundary } from '../api/write-boundary.js';
import { useCommand, useResource } from '../api/resource.js';
import { useSession } from '../session/session.jsx';
import { Button, Chip, Icon } from '../ui/primitives.jsx';
import { ErrorView, Resource } from '../ui/states.jsx';
import { inboxPointerText } from './inbox-presentation.js';
import { InboxDecision } from './InboxDecision.jsx';
import './inbox.css';

const KIND_FILTERS = [
  ['all', '전체'],
  ['unseen', '안 읽음'],
  ['question', '질문'],
  ['handoff', '넘김'],
  ['proposal', '제안'],
];

const KIND_CHIP = {
  question: { tone: 'waiting', label: '질문' },
  handoff: { tone: 'handoff', label: '넘김' },
  proposal: { tone: 'human', label: '제안' },
  version: { tone: 'accepted', label: '버전' },
};

export function InboxPane({ spaceId, home, archived }) {
  const session = useSession();
  const scope = `${session.nodeId}:${spaceId}`;
  const observedTail = useRef({ scope, cursor: null });
  if (observedTail.current.scope !== scope) observedTail.current = { scope, cursor: null };
  const [selected, setSelected] = useState(null);
  const selectionButton = useRef(null);
  const closeDecision = () => setSelected(null);
  useEffect(() => { if (!selected) selectionButton.current?.focus(); }, [selected]);
  const [filter, setFilter] = useState('all');
  const [cursorTrail, setCursorTrail] = useState([0]);
  const afterCursor = cursorTrail[cursorTrail.length - 1];
  const hasPreviousPage = cursorTrail.length > 1;
  const showNextPage = (nextCursor) => {
    if (typeof nextCursor !== 'number' || nextCursor <= afterCursor) return;
    setCursorTrail((trail) => [...trail, nextCursor]);
  };
  const showPreviousPage = () => setCursorTrail((trail) => trail.slice(0, -1));

  const inbox = useResource(`space:${spaceId}:inbox:${afterCursor}`, async (signal) => {
    const page = await read('inbox-page', {
      nodeId: session.nodeId, query: { spaceId, afterCursor, limit: 50 }, signal,
    });
    signal.throwIfAborted();
    if (observedTail.current.cursor === null && Number.isSafeInteger(page.latestCursor)) {
      observedTail.current.cursor = page.latestCursor;
    }
    return page;
  },
    { quiet: true, identity: session.nodeId },
  );
  // Task 제목 얹기 — 실패해도 수신함은 산다(곁가지 읽기).
  const board = useResource(`space:${spaceId}:tasks:first`, () =>
    read('task-board', { nodeId: session.nodeId, query: { spaceId, limit: 100 } }),
  );
  const titles = board.status === 'ready'
    ? new Map((board.data.tasks ?? []).map((task) => [task.taskId, task.title]))
    : new Map();

  const markSeen = useCommand(
    (params, idempotencyKey) => command('inbox_mark_seen', params, { nodeId: session.nodeId, idempotencyKey }),
    { invalidates: [`space:${spaceId}:inbox`] },
  );

  return (
    <section class="work work--inbox">
      <header class="work__head work__head--inbox">
        <div class="work__head-text">
          <h1 class="t-view">수신함</h1>
          <p class="t-meta">나를 기다리는 질문·넘김·제안만 모읍니다.</p>
        </div>
        <Button size="sm" tone="ghost" onClick={inbox.reload}>새로 확인</Button>
      </header>

      <div class="work__body inbox-pane">
        <nav class="inbox-tabs" aria-label="수신함 필터">
          {KIND_FILTERS.map(([key, label]) => (
            <button type="button" key={key} class="fpill" aria-pressed={filter === key} onClick={() => setFilter(key)}>
              {label}
            </button>
          ))}
        </nav>

        {markSeen.error ? <ErrorView error={markSeen.error} /> : null}
        <div class={`inbox-workspace${selected ? ' inbox-workspace--selected' : ''}`}>
        <div class="inbox-list">
        <Resource resource={inbox} loadingLabel="수신함을 읽는 중">
          {(data) => {
            const pointers = (data.pointers ?? []).filter((p) => {
              if (filter === 'unseen') return !p.seenAt;
              if (filter === 'all') return true;
              return p.kind === filter;
            });
            const tailKnown = Number.isSafeInteger(data.latestCursor);
            const hasNext = typeof data.nextCursor === 'number' && data.nextCursor > afterCursor
              && (tailKnown ? data.nextCursor < data.latestCursor : (data.pointers ?? []).length === 50);
            const newTail = tailKnown && observedTail.current.cursor !== null && data.latestCursor > observedTail.current.cursor;
            let empty = null;
            if (pointers.length === 0) {
              const filtered = (data.pointers ?? []).length > 0;
              empty = <InboxEmpty
                title={filtered ? '이 필터에는 새 소식이 없습니다.' : hasPreviousPage ? '이 페이지에는 더 새 포인터가 없습니다.' : '수신함이 비었습니다.'}
                copy={filtered ? (hasNext ? '다른 필터나 다음 페이지에서 이어서 확인할 수 있습니다.' : '다른 필터에서 이어서 확인할 수 있습니다.') : hasPreviousPage ? '앞 페이지에 포인터가 있을 수 있습니다.' : '지금 확인할 질문이나 넘김이 없습니다.'}
                action={hasPreviousPage ? <Button size="sm" onClick={showPreviousPage}>이전 것 보기</Button> : null}
              />;
            }
            return (
              <>
                {newTail ? <div class="row">
                  <p class="t-meta">새 요청·응답이 있습니다.</p>
                  <Button size="sm" disabled={inbox.isStale} onClick={() => {
                    const from = observedTail.current.cursor;
                    observedTail.current.cursor = data.latestCursor;
                    if (from !== afterCursor) setCursorTrail((trail) => [...trail, from]);
                    else inbox.reload();
                  }}>새 요청부터 보기</Button>
                </div> : null}
                {empty}
                <section class="mbox" aria-label="수신함 포인터">
                  {pointers.map((p) => {
                    const text = inboxPointerText(p);
                    const known = text !== p.kind && text !== p.reason;
                    const chip = KIND_CHIP[p.kind] ?? null;
                    const title = p.taskId ? titles.get(p.taskId) : null;
                    const at = p.occurredAt ?? p.createdAt ?? null;
                    return (
                      <div class={`mbox__row${p.seenAt ? '' : ' mbox__row--unseen'}${selected?.eventId === p.eventId ? ' mbox__row--selected' : ''}`} key={p.eventId}>
                        {p.seenAt ? <span aria-hidden="true" /> : <span class="mbox__dot" aria-hidden="true" title="안 읽음" />}
                        <span>{chip ? <Chip tone={chip.tone}>{chip.label}</Chip> : <Chip><span class="t-mono">{p.kind}</span></Chip>}</span>
                        {/* 원문으로 가기와 읽음 표시는 별개의 행위 — 동등한 형제로 둔다(E-2). */}
                        {p.taskId ? (
                        <button type="button"
                          ref={(element) => { if (element && selected?.eventId === p.eventId) selectionButton.current = element; }}
                          class={`mbox__sentence t-body${known ? '' : ' t-mono'}`}
                          aria-expanded={selected?.eventId === p.eventId}
                          onClick={(event) => { selectionButton.current = event.currentTarget; setSelected(p); }}
                        >
                          {title ? <><span class="mbox__task">{title}</span> — </> : null}
                          {text}
                        </button>
                        ) : (
                        <span class={`mbox__sentence t-body${known ? '' : ' t-mono'}`}>
                          {title ? <><span class="mbox__task">{title}</span> — </> : null}
                          {text}
                        </span>
                        )}
                        <span class="mbox__trail">
                          {at ? <time class="t-meta t-mono" dateTime={at}>{formatWhen(at)}</time> : null}
                          {p.seenAt ? <span class="t-meta">읽음</span> : (
                            <Button size="xs" tone="ghost" busy={markSeen.pending}
                              disabled={inbox.isStale || markSeen.blocked}
                              onClick={() => { if (inbox.canWrite()) markSeen.run({ spaceId, eventId: p.eventId }); }}>
                              읽음 표시
                            </Button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </section>

                {/* 다음 커서가 실제로 있을 때만 그린다 (D-7). */}
                {hasNext || hasPreviousPage ? (
                  <div class="row" style="padding: var(--space-3) 0">
                    {hasPreviousPage && !empty ? <Button size="sm" disabled={inbox.isStale} onClick={showPreviousPage}>이전 것 보기</Button> : null}
                    {hasNext ? <Button size="sm" disabled={inbox.isStale} onClick={() => showNextPage(data.nextCursor)}>
                      다음 것 더 읽기
                    </Button> : null}
                  </div>
                ) : null}
              </>
            );
          }}
        </Resource>
        </div>
        {selected && inbox.status === 'ready' ? <WriteBoundary resources={[inbox]}><InboxDecision key={`${session.nodeId}:${selected.eventId}`} pointer={selected}
          spaceId={spaceId} home={home} archived={archived} close={closeDecision} /></WriteBoundary> : null}
        </div>
      </div>
    </section>
  );
}

function InboxEmpty({ title, copy, action }) {
  return (
    <section class="inbox-empty" aria-live="polite">
      <span class="inbox-empty__mark" aria-hidden="true"><Icon name="inbox" /></span>
      <div class="inbox-empty__copy">
        <h2 class="t-body-strong">{title}</h2>
        <p class="t-meta">{copy}</p>
      </div>
      {action ? <div class="inbox-empty__action">{action}</div> : null}
    </section>
  );
}

function formatWhen(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = new Date().toDateString() === d.toDateString();
  return new Intl.DateTimeFormat('ko-KR', sameDay
    ? { hour: '2-digit', minute: '2-digit' }
    : { month: 'numeric', day: 'numeric' }).format(d);
}
